import {useEffect, useEffectEvent, useRef, useState} from 'react'

import {usePrefersReducedMotion} from '@github-ui/use-prefers-reduced-motion'

import type {HeroPaginationProps} from './types'

export function useHeroPagination({
  currentChapterIndex,
  items,
  onChange,
  playback,
}: Pick<HeroPaginationProps, 'currentChapterIndex' | 'items' | 'onChange' | 'playback'>) {
  const prefersReducedMotion = usePrefersReducedMotion()
  const [pausedOverride, setPausedOverride] = useState<boolean | null>(null)
  const [restartCount, setRestartCount] = useState(0)
  const [finishedChapter, setFinishedChapter] = useState<{index: number; duration: number | undefined} | null>(null)
  const isPaused = playback?.isPaused ?? pausedOverride ?? prefersReducedMotion
  const viewportRef = useRef<HTMLDivElement>(null)
  const selectedButtonRef = useRef<HTMLButtonElement>(null)
  const progressRef = useRef<HTMLSpanElement>(null)
  const animationRef = useRef<Animation | null>(null)
  const duration = items[currentChapterIndex]?.duration
  const isFinished = playback?.isFinished ?? finishedChapter !== null
  const isControlled = playback !== undefined
  const elapsed = playback?.elapsed
  const isPlaying = playback?.isPlaying ?? !isPaused
  const action: keyof HeroPaginationProps['a11y'] = isFinished ? 'replay' : isPaused ? 'play' : 'pause'

  if (
    finishedChapter &&
    (finishedChapter.index !== currentChapterIndex ||
      finishedChapter.duration !== duration ||
      currentChapterIndex !== items.length - 1)
  ) {
    setFinishedChapter(null)
  }

  const advanceChapter = useEffectEvent(() => {
    if (isPaused) return

    if (currentChapterIndex === items.length - 1) {
      setFinishedChapter({index: currentChapterIndex, duration})
    } else {
      onChange(currentChapterIndex + 1)
    }
  })

  useEffect(() => {
    const progress = progressRef.current
    if (!progress || !duration || duration <= 0) return

    const animation = progress.animate([{transform: 'scaleX(0)'}, {transform: 'scaleX(1)'}], {
      duration: duration * 1000,
      easing: 'linear',
      fill: 'forwards',
    })
    animation.pause()
    if (!isControlled) animation.onfinish = advanceChapter
    animationRef.current = animation

    return () => {
      animation.onfinish = null
      animation.cancel()
      animationRef.current = null
    }
  }, [currentChapterIndex, duration, items.length, restartCount, isControlled])

  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- Synchronize the Web Animation with the video playback time.
    if (elapsed !== undefined && duration) {
      if (animationRef.current) animationRef.current.currentTime = Math.max(0, Math.min(elapsed, duration)) * 1000
    }
    if (!isPlaying) {
      animationRef.current?.pause()
    } else {
      animationRef.current?.play()
    }
  }, [isPlaying, elapsed, currentChapterIndex, duration, items.length, restartCount, isControlled])

  useEffect(() => {
    const viewport = viewportRef.current
    const button = selectedButtonRef.current
    if (!viewport || !button) return

    const viewportBounds = viewport.getBoundingClientRect()
    const buttonBounds = button.getBoundingClientRect()
    const scrollPadding = getComputedStyle(viewport).scrollPaddingInlineEnd
    const visibleRight = viewportBounds.right - parseFloat(scrollPadding)
    const offset =
      buttonBounds.left < viewportBounds.left
        ? buttonBounds.left - viewportBounds.left
        : Math.max(0, buttonBounds.right - visibleRight)

    if (offset !== 0) {
      viewport.scrollBy({left: offset, behavior: prefersReducedMotion ? 'instant' : 'smooth'})
    }
  }, [currentChapterIndex, prefersReducedMotion])

  const selectChapter = (index: number) => {
    setFinishedChapter(null)
    setRestartCount(count => count + 1)
    onChange(index)
  }

  const handlePlaybackClick = () => {
    if (playback) {
      playback.onToggle()
      return
    }
    if (isFinished) {
      selectChapter(0)
      setPausedOverride(false)
    } else {
      setPausedOverride(!isPaused)
    }
  }

  return {action, viewportRef, selectedButtonRef, progressRef, selectChapter, handlePlaybackClick}
}
